import { useEffect, useRef, useState } from "react"

function generateTimeOptions(interval: number = 30): string[] {
  const options: string[] = []
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += interval) {
      const h = String(hour).padStart(2, "0")
      const m = String(minute).padStart(2, "0")
      options.push(`${h}:${m}`)
    }
  }
  return options
}

function formatTime12Hour(time24: string): string {
  const [hours, minutes] = time24.split(":").map(Number)
  if (hours === undefined || minutes === undefined) return time24
  const period = hours >= 12 ? "PM" : "AM"
  const hour12 = hours % 12 || 12
  return `${hour12}:${String(minutes).padStart(2, "0")} ${period}`
}

interface TimePickerProps {
  label?: string
  value: string // HH:mm format
  onChange: (value: string) => void
  disabled?: boolean
  placeholder?: string
  interval?: number // minutes between options (default 30)
}

export function TimePicker({
  label,
  value,
  onChange,
  disabled = false,
  placeholder = "Select time",
  interval = 30,
}: TimePickerProps) {
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const options = generateTimeOptions(interval)
  const selectedIndex = Math.max(0, options.indexOf(value))

  useEffect(() => {
    if (!open) return

    function handleOutsidePointerDown(event: PointerEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }

    document.addEventListener("pointerdown", handleOutsidePointerDown)
    return () => document.removeEventListener("pointerdown", handleOutsidePointerDown)
  }, [open])

  useEffect(() => {
    if (open && listRef.current) {
      const activeButton = listRef.current.querySelector(`[data-index="${activeIndex}"]`)
      activeButton?.scrollIntoView({ block: "nearest" })
    }
  }, [open, activeIndex])

  function handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return

    if (!open && (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Enter" || event.key === " ")) {
      event.preventDefault()
      setActiveIndex(selectedIndex)
      setOpen(true)
      return
    }

    if (open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      event.preventDefault()
      const direction = event.key === "ArrowDown" ? 1 : -1
      setActiveIndex((index) => Math.min(options.length - 1, Math.max(0, index + direction)))
      return
    }

    if (event.key === "Home" || event.key === "End") {
      event.preventDefault()
      setOpen(true)
      setActiveIndex(event.key === "Home" ? 0 : Math.max(0, options.length - 1))
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      setOpen(false)
      return
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      if (open) {
        const selected = options[activeIndex]
        if (selected) onChange(selected)
        setOpen(false)
      } else {
        setActiveIndex(selectedIndex)
        setOpen(true)
      }
    }
  }

  const displayValue = value ? formatTime12Hour(value) : placeholder

  return (
    <div ref={containerRef} className="relative">
      {label && <span className="mb-1.5 block text-sm font-medium text-ink">{label}</span>}
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          setActiveIndex(selectedIndex)
          setOpen((isOpen) => !isOpen)
        }}
        onKeyDown={handleKeyDown}
        className={`flex w-full items-center justify-between rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-left text-sm transition-colors hover:border-ink/30 focus:border-forest-700 focus:outline-none disabled:opacity-60 ${
          value ? "text-ink" : "text-ink/40"
        }`}
      >
        <span className="truncate">{displayValue}</span>
        <svg viewBox="0 0 24 24" className="h-5 w-5 text-ink/50" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 6v6l4 2" />
        </svg>
      </button>
      <div
        ref={listRef}
        role="listbox"
        aria-hidden={!open}
        className={`absolute inset-x-0 top-full z-50 mt-1 max-h-52 overflow-y-auto rounded-lg border border-gold-500 bg-white p-1 shadow-lg transition-[opacity,transform,visibility] duration-200 ease-out ${
          open ? "visible translate-y-0 opacity-100" : "pointer-events-none invisible -translate-y-1 opacity-0"
        }`}
      >
        {options.map((option, index) => {
          const isSelected = option === value
          const isActive = index === activeIndex
          return (
            <button
              key={option}
              type="button"
              role="option"
              data-index={index}
              aria-selected={isSelected}
              tabIndex={-1}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => {
                onChange(option)
                setActiveIndex(index)
                setOpen(false)
                buttonRef.current?.focus()
              }}
              className={`block w-full rounded px-3 py-2 text-left text-sm text-ink transition-colors duration-150 ease-out ${
                isActive ? "bg-gold-500/30" : isSelected ? "bg-gold-500/20" : "hover:bg-cream-100"
              }`}
            >
              {formatTime12Hour(option)}
            </button>
          )
        })}
      </div>
    </div>
  )
}
